/**
 * BatchOnboardingConsole — Team / Folder Structure Batch Creator. For onboarding
 * a big customer: bulk-create teams, list teams, and bulk-create folders against
 * the customer's account via their OptiSigns API key.
 *
 * Master / detail: a landing page lists the three tools; picking one opens its
 * own sub-view with an "OptiSigns Credentials" section (the per-tool API key)
 * and a "CSV Data" section — Download Template, Upload CSV, and an editable
 * "CSV Content" box (upload fills the box; you can also paste/edit directly).
 * Create/List read from that box.
 *
 * Frontend only for now: the create/list calls are stubbed in useBatchOnboarding
 * pending the staff-api server-side path (the API key must stay off the browser
 * and out of the audit log — see that hook's header). CSV parsing, template
 * download, the editable box, and results UI are real.
 */
import { useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import {
  FOLDER_TEMPLATE_CSV,
  TEAM_TEMPLATE_CSV,
  downloadCsv,
  parseFolderCsv,
  parseTeamCsv,
  toCsv,
} from '../batch/csv';
import {
  useBatchOnboarding,
  type BatchRowResult,
  type TeamListItem,
} from '../hooks/useBatchOnboarding';

const TEXTAREA_CLASS =
  'flex min-h-[180px] w-full rounded-md border border-input bg-transparent dark:bg-card px-3 py-2 font-mono text-xs transition-colors hover:border-muted-foreground/70 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

type Tool = 'create-teams' | 'list-teams' | 'create-folders';
type Msg = { kind: 'ok' | 'err'; text: string } | null;

// ── Shared bits ─────────────────────────────────────────────────────────────
interface CardShellProps {
  testId: string;
  title: string;
  description: string;
  children: React.ReactNode;
}
function CardShell({ testId, title, description, children }: CardShellProps) {
  return (
    <div
      className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
      data-testid={testId}
    >
      <div className="flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}

function ResultLine({ msg, testId }: { msg: Msg; testId: string }) {
  if (!msg) return null;
  return (
    <p
      className={msg.kind === 'ok' ? 'text-xs text-muted-foreground' : 'text-xs text-destructive'}
      data-testid={testId}
    >
      {msg.text}
    </p>
  );
}

/** OptiSigns Credentials card — per-tool API key, plus any extra fields. */
function CredentialsCard({
  apiKey,
  setApiKey,
  description,
  apiKeyLabel = 'API Key',
  helper,
  children,
}: {
  apiKey: string;
  setApiKey: (v: string) => void;
  description: string;
  apiKeyLabel?: string;
  helper?: string;
  children?: React.ReactNode;
}) {
  return (
    <CardShell
      testId="batch-credentials"
      title="OptiSigns Credentials"
      description={description}
    >
      <div className="flex flex-col gap-1.5 md:max-w-lg">
        <Label htmlFor="batch-apikey" className="text-xs">
          {apiKeyLabel}
        </Label>
        <Input
          id="batch-apikey"
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter your OptiSigns API Key"
          autoComplete="off"
          data-testid="batch-apikey"
        />
        {helper ? <p className="text-[11px] text-muted-foreground">{helper}</p> : null}
      </div>
      {children}
    </CardShell>
  );
}

/** CSV Data card — Download Template, Upload CSV, and the editable CSV box. */
function CsvDataCard({
  keyId,
  template,
  templateFilename,
  csvText,
  setCsvText,
  onChange,
}: {
  keyId: string;
  template: string;
  templateFilename: string;
  csvText: string;
  setCsvText: (v: string) => void;
  onChange?: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <CardShell
      testId={`${keyId}-csv-card`}
      title="CSV Data"
      description="Download the template, upload a CSV file, or paste CSV content directly."
    >
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          data-testid={`${keyId}-template`}
          onClick={() => downloadCsv(templateFilename, template)}
        >
          {'Download Template'}
        </Button>
        <input
          ref={ref}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          data-testid={`${keyId}-upload`}
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setCsvText(await file.text());
            onChange?.();
            e.target.value = ''; // allow re-selecting the same file
          }}
        />
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          data-testid={`${keyId}-upload-btn`}
          onClick={() => ref.current?.click()}
        >
          {'Upload CSV'}
        </Button>
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={`${keyId}-csv`} className="text-xs">
          {'CSV Content (or paste here)'}
        </Label>
        <textarea
          id={`${keyId}-csv`}
          className={TEXTAREA_CLASS}
          value={csvText}
          spellCheck={false}
          onChange={(e) => {
            setCsvText(e.target.value);
            onChange?.();
          }}
          data-testid={`${keyId}-csv`}
        />
      </div>
    </CardShell>
  );
}

function ResultsTable({ results, testId }: { results: BatchRowResult[]; testId: string }) {
  if (results.length === 0) return null;
  const okCount = results.filter((r) => r.ok).length;
  return (
    <CardShell testId={testId} title="Results" description={`${okCount} of ${results.length} created.`}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">{'Row'}</TableHead>
            <TableHead className="w-24">{'Status'}</TableHead>
            <TableHead>{'Name'}</TableHead>
            <TableHead>{'Result'}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {results.map((r, i) => (
            <TableRow key={`${r.name}-${i}`}>
              <TableCell className="text-muted-foreground">{r.rowNumber}</TableCell>
              <TableCell>
                <Badge variant={r.ok ? 'secondary' : 'destructive'}>
                  {r.ok ? 'Created' : 'Failed'}
                </Badge>
              </TableCell>
              <TableCell className="font-medium">{r.name || '—'}</TableCell>
              <TableCell className="text-muted-foreground">
                {r.ok && r.id ? `${r.message} (${r.id})` : r.message}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardShell>
  );
}

function DetailHeader({ title, subtitle, onBack }: { title: string; subtitle: string; onBack: () => void }) {
  return (
    <div className="flex flex-col gap-2">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="w-fit cursor-pointer px-2"
        data-testid="batch-back"
        onClick={onBack}
      >
        {'← Back'}
      </Button>
      <div className="flex flex-col gap-1">
        <h3 className="text-base font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}

// ── Tool 1: Create Teams ────────────────────────────────────────────────────
function CreateTeamsView({ onBack }: { onBack: () => void }) {
  const KEY = 'batch-create-teams';
  const { createTeams } = useBatchOnboarding();
  const [apiKey, setApiKey] = useState('');
  const [csvText, setCsvText] = useState(TEAM_TEMPLATE_CSV);
  const [results, setResults] = useState<BatchRowResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  const rows = useMemo(() => parseTeamCsv(csvText), [csvText]);
  const valid = apiKey.trim().length > 0 && rows.length > 0;

  const run = async () => {
    setBusy(true);
    setMsg(null);
    try {
      setResults(await createTeams(apiKey, rows));
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <DetailHeader
        title="Create Teams"
        subtitle="Batch create teams in OptiSigns using a CSV file."
        onBack={onBack}
      />
      <CredentialsCard
        apiKey={apiKey}
        setApiKey={setApiKey}
        description="Enter your OptiSigns API Key to authenticate requests."
        helper="This key will be used to authenticate with the OptiSigns GraphQL API."
      />
      <CsvDataCard
        keyId={KEY}
        template={TEAM_TEMPLATE_CSV}
        templateFilename="teams-template.csv"
        csvText={csvText}
        setCsvText={setCsvText}
        onChange={() => setResults([])}
      />
      <div className="flex items-center gap-2">
        <Button onClick={run} disabled={!valid || busy} className="cursor-pointer" data-testid={`${KEY}-submit`}>
          {busy ? 'Working...' : 'Create Teams'}
        </Button>
        <span className="text-xs text-muted-foreground" data-testid={`${KEY}-count`}>
          {`${rows.length} row(s) parsed`}
        </span>
      </div>
      <ResultLine msg={msg} testId={`${KEY}-error`} />
      <ResultsTable results={results} testId={`${KEY}-results`} />
    </div>
  );
}

// ── Tool 2: List Teams ──────────────────────────────────────────────────────
function ListTeamsView({ onBack }: { onBack: () => void }) {
  const KEY = 'batch-list-teams';
  const { fetchTeams } = useBatchOnboarding();
  const [apiKey, setApiKey] = useState('');
  const [teams, setTeams] = useState<TeamListItem[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  const fetch = async () => {
    setBusy(true);
    setMsg(null);
    try {
      setTeams(await fetchTeams(apiKey));
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };
  const download = () => {
    if (!teams) return;
    const csv = toCsv(
      ['name', 'id', 'description', 'usedScreens', 'maxScreens'],
      teams.map((t) => [
        t.name,
        t.id,
        t.description ?? '',
        t.usedScreens == null ? '' : String(t.usedScreens),
        t.maxScreens == null ? '' : String(t.maxScreens),
      ]),
    );
    downloadCsv('teams.csv', csv);
  };

  return (
    <div className="flex flex-col gap-4">
      <DetailHeader
        title="List All Teams"
        subtitle="View all teams with their IDs."
        onBack={onBack}
      />
      <CredentialsCard
        apiKey={apiKey}
        setApiKey={setApiKey}
        description="Enter your OptiSigns API Key to authenticate requests."
        helper="This key will be used to authenticate with the OptiSigns GraphQL API."
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button
          onClick={fetch}
          disabled={apiKey.trim().length === 0 || busy}
          className="cursor-pointer"
          data-testid={`${KEY}-fetch`}
        >
          {busy ? 'Working...' : 'Fetch Teams'}
        </Button>
        <Button
          onClick={download}
          disabled={!teams || teams.length === 0}
          variant="outline"
          className="cursor-pointer"
          data-testid={`${KEY}-download`}
        >
          {'Download CSV'}
        </Button>
      </div>
      <ResultLine msg={msg} testId={`${KEY}-error`} />
      <CardShell testId={`${KEY}-card`} title="Teams" description="All teams in your OptiSigns account.">
        {teams == null ? (
          <p className="text-xs text-muted-foreground" data-testid={`${KEY}-empty`}>
            {'No teams loaded. Enter API key and click Fetch Teams.'}
          </p>
        ) : (
          <Table data-testid={`${KEY}-table`}>
            <TableHeader>
              <TableRow>
                <TableHead>{'Team Name'}</TableHead>
                <TableHead>{'Team ID'}</TableHead>
                <TableHead>{'Description'}</TableHead>
                <TableHead className="w-24">{'Used Screens'}</TableHead>
                <TableHead className="w-24">{'Max Screens'}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {teams.map((t) => (
                <TableRow key={t.id}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.id}</TableCell>
                  <TableCell className="text-muted-foreground">{t.description || '—'}</TableCell>
                  <TableCell>{t.usedScreens == null ? '—' : t.usedScreens}</TableCell>
                  <TableCell>{t.maxScreens == null ? '—' : t.maxScreens}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardShell>
    </div>
  );
}

// ── Tool 3: Create Folders ──────────────────────────────────────────────────
function CreateFoldersView({ onBack }: { onBack: () => void }) {
  const KEY = 'batch-create-folders';
  const { createFolders } = useBatchOnboarding();
  const [apiKey, setApiKey] = useState('');
  const [teamId, setTeamId] = useState('1');
  const [csvText, setCsvText] = useState(FOLDER_TEMPLATE_CSV);
  const [results, setResults] = useState<BatchRowResult[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(null);

  const rows = useMemo(() => parseFolderCsv(csvText), [csvText]);
  const valid = apiKey.trim().length > 0 && teamId.trim().length > 0 && rows.length > 0;

  const run = async () => {
    setBusy(true);
    setMsg(null);
    try {
      setResults(await createFolders(apiKey, teamId.trim(), rows));
    } catch (e) {
      setMsg({ kind: 'err', text: (e as Error)?.message ?? 'Failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <DetailHeader
        title="Create Folders"
        subtitle="Batch create groups for Devices, Assets, Playlists, and Schedules."
        onBack={onBack}
      />
      <CredentialsCard
        apiKey={apiKey}
        setApiKey={setApiKey}
        description="Enter your OptiSigns API credentials."
        apiKeyLabel="OptiSigns API Key"
      >
        <div className="flex flex-col gap-1.5 md:max-w-xs">
          <Label htmlFor={`${KEY}-teamid`} className="text-xs">
            {'Team ID (Optional, default: 1)'}
          </Label>
          <Input
            id={`${KEY}-teamid`}
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            placeholder="1"
            data-testid={`${KEY}-teamid`}
          />
        </div>
      </CredentialsCard>
      <CsvDataCard
        keyId={KEY}
        template={FOLDER_TEMPLATE_CSV}
        templateFilename="folders-template.csv"
        csvText={csvText}
        setCsvText={setCsvText}
        onChange={() => setResults([])}
      />
      <div className="flex items-center gap-2">
        <Button onClick={run} disabled={!valid || busy} className="cursor-pointer" data-testid={`${KEY}-submit`}>
          {busy ? 'Working...' : 'Create Folders'}
        </Button>
        <span className="text-xs text-muted-foreground" data-testid={`${KEY}-count`}>
          {`${rows.length} row(s) parsed`}
        </span>
      </div>
      <ResultLine msg={msg} testId={`${KEY}-error`} />
      <ResultsTable results={results} testId={`${KEY}-results`} />
    </div>
  );
}

// ── Landing ─────────────────────────────────────────────────────────────────
interface ToolCard {
  tool: Tool;
  title: string;
  subtitle: string;
  body: string;
  button: string;
}
const TOOLS: ToolCard[] = [
  {
    tool: 'create-teams',
    title: 'Create Teams',
    subtitle: 'Batch create teams in OptiSigns using a CSV file.',
    body: 'Upload a CSV file to create multiple teams at once.',
    button: 'Create Teams',
  },
  {
    tool: 'list-teams',
    title: 'List Teams',
    subtitle: 'View all teams with their IDs and export to CSV.',
    body: 'Fetch all teams from OptiSigns and view their names and IDs. Export to CSV format.',
    button: 'List All Teams',
  },
  {
    tool: 'create-folders',
    title: 'Create Folders',
    subtitle: 'Batch create groups for Devices, Assets, Playlists, and Schedules.',
    body: 'Upload a CSV file to create multiple folders/groups at once. Supports nested folders.',
    button: 'Create Folders',
  },
];

export function BatchOnboardingConsole() {
  const [tool, setTool] = useState<Tool | null>(null);

  if (tool === 'create-teams') {
    return (
      <div data-testid="batch-onboarding-console">
        <CreateTeamsView onBack={() => setTool(null)} />
      </div>
    );
  }
  if (tool === 'list-teams') {
    return (
      <div data-testid="batch-onboarding-console">
        <ListTeamsView onBack={() => setTool(null)} />
      </div>
    );
  }
  if (tool === 'create-folders') {
    return (
      <div data-testid="batch-onboarding-console">
        <CreateFoldersView onBack={() => setTool(null)} />
      </div>
    );
  }

  return (
    <div
      className="grid grid-cols-1 gap-4 md:grid-cols-3"
      data-testid="batch-onboarding-console"
    >
      {TOOLS.map((t) => (
        <div
          key={t.tool}
          className="flex flex-col gap-3 rounded-lg border bg-card p-4 shadow-sm"
          data-testid={`batch-home-card-${t.tool}`}
        >
          <div className="flex flex-col gap-1">
            <h3 className="text-sm font-semibold text-foreground">{t.title}</h3>
            <p className="text-xs text-muted-foreground">{t.subtitle}</p>
          </div>
          <p className="flex-1 text-xs text-muted-foreground">{t.body}</p>
          <Button
            className="w-fit cursor-pointer"
            data-testid={`batch-home-${t.tool}`}
            onClick={() => setTool(t.tool)}
          >
            {t.button}
          </Button>
        </div>
      ))}
    </div>
  );
}
